import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './BillingRules.css';
import BillingRuleEditor from './BillingRuleEditor';
import { getAccessToken } from '../utils/auth';
import { getBillingRules, deleteBillingRule } from '../utils/httpApi';

/** Empty rule template used when the user clicks "+ Add rule". */
function emptyRule() {
  return {
    id: 0, airportId: 0,
    ruleCode: '', category: '', description: '',
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: null,
    calculationBasis: '', rate: 0, currency: 'EUR',
    minCharge: null, maxCharge: null,
    minChargeSeatsBelow: null, freePeriodMinutes: null, chargeUnitMinutes: null,
    appliesToRuleCode: null, priority: 50,
    notes: null, sourceRef: null, deleted: false,
  };
}

/**
 * Billing rules page — read-only view of the tariff rules in force at a
 * given airport on a chosen date. Each rule's effective_from / effective_to
 * window IS the version, so picking a date that falls inside a different
 * version shows that version's rates. Charges, basis, free periods and
 * priority are all surfaced; rule conditions are summarised inline.
 *
 * Admin-gated via the underlying BillingService.getBillingRules endpoint.
 */

function todayIso() { return new Date().toISOString().slice(0, 10); }

function apiOk(resp) {
  const s = resp && resp.header && resp.header.apiStatus;
  return s === 'OK' || s === 'SUCCESS';
}

function money(amount, currency) {
  if (amount === null || amount === undefined) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency', currency: currency || 'EUR',
      maximumFractionDigits: 4
    }).format(n);
  } catch {
    return (currency || 'EUR') + ' ' + n.toFixed(2);
  }
}

function fmtDate(s) {
  if (!s) return '—';
  // backend serialises LocalDate as YYYY-MM-DD already; show "open" for null end
  return s;
}

function BillingRules({ airportIcao }) {
  const [onDate, setOnDate] = useState(todayIso());
  const [category, setCategory] = useState('');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(null); // BillingRule being edited/created, or null

  const load = useCallback(() => {
    const token = getAccessToken();
    if (!token) { setStatus('Not authenticated.'); return; }
    setLoading(true);
    setStatus(null);
    getBillingRules({ airportIcao, onDate, category: category || undefined }, token)
      .then((resp) => {
        if (!apiOk(resp)) {
          const errs = resp && resp.header && resp.header.errors;
          setStatus('Load failed: ' + (errs && errs[0] ? errs[0].description : 'error'));
          setRules([]);
          return;
        }
        setRules(resp.rules || []);
      })
      .catch((e) => { setStatus('Load error: ' + e.message); setRules([]); })
      .finally(() => setLoading(false));
  }, [airportIcao, onDate, category]);

  useEffect(() => { load(); }, [load]);

  function handleDelete(rule) {
    if (!window.confirm('Soft-delete rule ' + rule.ruleCode + '?\n\n'
        + 'It will be hidden from the active set but the row stays in the '
        + 'database with is_deleted=1 and the deletion is audited.')) return;
    const token = getAccessToken();
    if (!token) { setStatus('Not authenticated.'); return; }
    deleteBillingRule({ airportIcao, ruleId: rule.id }, token)
      .then((resp) => {
        if (!apiOk(resp)) {
          const errs = resp && resp.header && resp.header.errors;
          setStatus('Delete failed: '
            + (errs && errs[0] ? errs[0].description : 'error'));
          return;
        }
        load();
      })
      .catch((e) => setStatus('Delete error: ' + e.message));
  }

  // Group rules by their (effective_from → effective_to) version so the user
  // can see at a glance which tariff version is in force on the chosen date.
  // Most active rules will share the same version, but a few may differ
  // (e.g. when a single rule was reissued mid-period).
  const versions = useMemo(() => {
    const m = new Map();
    rules.forEach((r) => {
      const key = (r.effectiveFrom || '?') + ' → ' + (r.effectiveTo || 'open');
      if (!m.has(key)) m.set(key, { key, from: r.effectiveFrom, to: r.effectiveTo, count: 0 });
      m.get(key).count++;
    });
    return [...m.values()].sort((a, b) => (a.from || '').localeCompare(b.from || ''));
  }, [rules]);

  // Distinct categories present in this set, for the category dropdown.
  const categories = useMemo(
    () => [...new Set(rules.map((r) => r.category).filter(Boolean))].sort(),
    [rules]
  );

  return (
    <div className="brules">
      <div className="brules-controls">
        <label className="brules-field">
          <span>On date</span>
          <input type="date" value={onDate} onChange={(e) => setOnDate(e.target.value)} />
        </label>
        <label className="brules-field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <div className="brules-versions">
          {versions.map((v) => (
            <span key={v.key} className="brules-version-chip">
              <span className="brules-version-range">{fmtDate(v.from)} → {fmtDate(v.to)}</span>
              <span className="brules-version-count">{v.count} rule{v.count === 1 ? '' : 's'}</span>
            </span>
          ))}
          {versions.length === 0 && !loading && (
            <span className="brules-version-chip brules-version-empty">
              No tariff version active on {onDate}
            </span>
          )}
        </div>
        <button type="button" className="brules-add-btn"
          onClick={() => setEditing(emptyRule())}>+ Add rule</button>
      </div>

      {status && <div className="brules-status">{status}</div>}
      {loading && <div className="brules-status">Loading rules…</div>}

      <div className="brules-table-wrap">
        <table className="brules-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Category</th>
              <th>Description</th>
              <th>Basis</th>
              <th className="num">Rate</th>
              <th className="num">Min</th>
              <th className="num">Max</th>
              <th className="num">Free</th>
              <th className="num">Pri</th>
              <th>Effective</th>
              <th className="brules-actions-th"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className={'brules-row brules-pri-' + (r.priority || 0)}>
                <td className="brules-code">{r.ruleCode}</td>
                <td><span className="brules-cat">{r.category}</span></td>
                <td className="brules-desc">{r.description || '—'}</td>
                <td className="brules-basis">{r.calculationBasis || '—'}</td>
                <td className="num">{money(r.rate, r.currency)}</td>
                <td className="num">{r.minCharge != null ? money(r.minCharge, r.currency) : '—'}</td>
                <td className="num">{r.maxCharge != null ? money(r.maxCharge, r.currency) : '—'}</td>
                <td className="num">
                  {r.freePeriodMinutes != null ? r.freePeriodMinutes + ' min' : '—'}
                </td>
                <td className="num">{r.priority}</td>
                <td className="brules-eff">
                  <span className="brules-eff-from">{fmtDate(r.effectiveFrom)}</span>
                  <span className="brules-eff-arrow">→</span>
                  <span className="brules-eff-to">{fmtDate(r.effectiveTo) || 'open'}</span>
                </td>
                <td className="brules-actions">
                  <button type="button" className="brules-action"
                    onClick={() => setEditing({ ...r })} title="Edit">✎</button>
                  <button type="button" className="brules-action brules-action-danger"
                    onClick={() => handleDelete(r)} title="Soft-delete">🗑</button>
                </td>
              </tr>
            ))}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={11} className="brules-empty">
                No rules active on {onDate}
                {category ? ' in category ' + category : ''}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <BillingRuleEditor
          airportIcao={airportIcao}
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

export default BillingRules;
